import type { PublicSnapshot } from "@paddock-signal/contracts";
import { CircuitAssetSchema } from "@paddock-signal/map-assets";

type CircuitSummary = PublicSnapshot["circuits"][number];

export interface RenderCircuitAsset {
  bankAngleDegrees?: number[];
  bankingAttribution?: string;
  bounds: [[number, number], [number, number]];
  centerline: [number, number, number][];
  circuitId: string;
  elevationAttribution?: string;
  geometryAttribution: string;
  mode: "3d" | "2d-fallback";
}

export class CircuitAssetError extends Error {
  constructor(message: string, options: { cause?: unknown } = {}) {
    super(message, options);
    this.name = "CircuitAssetError";
  }
}

function fromProductionAsset(input: unknown): RenderCircuitAsset {
  const asset = CircuitAssetSchema.parse(input);
  return {
    circuitId: asset.circuitId,
    mode: asset.mode,
    centerline: asset.centerline,
    bounds: asset.bounds,
    geometryAttribution: asset.geometrySource.attribution,
    ...(asset.bankAngleDegrees === undefined
      ? {}
      : { bankAngleDegrees: asset.bankAngleDegrees }),
    ...(asset.bankingSource === undefined
      ? {}
      : { bankingAttribution: asset.bankingSource.attribution }),
    ...(asset.elevationSource === undefined
      ? {}
      : { elevationAttribution: asset.elevationSource.attribution }),
  };
}

export async function loadCircuitAsset(
  circuit: CircuitSummary,
  fetcher: typeof fetch = fetch,
): Promise<RenderCircuitAsset> {
  if (circuit.assetState === "unavailable") {
    throw new CircuitAssetError(`Circuit ${circuit.id} has no verified asset.`);
  }
  if (circuit.id === "circuit-ceramique") {
    return import("./data/circuit-ceramique.asset.js").then(
      (module) => module.circuitCeramiqueAsset,
    );
  }
  let response: Response;
  try {
    response = await fetcher(
      `/circuits/${encodeURIComponent(circuit.id)}.json`,
      {
        headers: { Accept: "application/json" },
        signal: AbortSignal.timeout(8_000),
      },
    );
  } catch (error) {
    throw new CircuitAssetError(`Circuit ${circuit.id} request failed.`, {
      cause: error,
    });
  }
  if (!response.ok) {
    throw new CircuitAssetError(
      `Circuit ${circuit.id} returned HTTP ${String(response.status)}.`,
    );
  }
  if (!response.headers.get("content-type")?.includes("application/json")) {
    throw new CircuitAssetError(`Circuit ${circuit.id} must return JSON.`);
  }
  const asset = fromProductionAsset(await response.json());
  if (asset.circuitId !== circuit.id) {
    throw new CircuitAssetError(
      `Circuit ${circuit.id} returned a mismatched asset.`,
    );
  }
  if (asset.mode !== circuit.assetState) {
    throw new CircuitAssetError(`Circuit ${circuit.id} asset mode drifted.`);
  }
  return asset;
}
